import * as React from 'react';
import { FormFieldInput, InputAdornment } from '@cian/ui-kit';

import { IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';

import { SuggestPopup } from './components';
import { ISuggestion } from './types';

import * as styles from './styles.css';

export interface ISuggestFieldProps {
  inputValue: string;
  suggestions: ISuggestion[];
  isPopupShown: boolean;
  placeholder: string;
  isLoading: boolean;
  error?: string;
  notFoundText: string;
  onChange(value: string): void;
  onItemClick(value: ISuggestion): void;
  onKeyPress(e: React.KeyboardEvent): void;
  onBlur?: () => void;
}

export const SuggestField = React.forwardRef<HTMLDivElement, ISuggestFieldProps>((props, ref) => {
  const {
    onChange,
    inputValue,
    suggestions,
    onItemClick,
    isPopupShown,
    placeholder,
    isLoading,
    onKeyPress,
    error,
    notFoundText,
    onBlur,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange],
  );

  const handleClick = React.useCallback(
    (value: ISuggestion) => {
      onItemClick(value);
      /* istanbul ignore next */
      inputRef.current?.focus();
    },
    [inputRef, onItemClick],
  );

  return (
    <div ref={ref} className={styles['container']} data-testid="container-suggest">
      <div className={styles['input-container']}>
        <FormFieldInput
          loading={isLoading}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          size="M"
          inputRef={inputRef}
          value={inputValue}
          onChange={handleChange}
          onBlur={onBlur}
          leftAdornment={
            <InputAdornment>
              <IconActionSearch16 color="icon-primary-default" />
            </InputAdornment>
          }
          errorMessage={error}
          invalid={Boolean(error)}
        />
      </div>
      {isPopupShown && <SuggestPopup suggestions={suggestions} onItemClick={handleClick} notFoundText={notFoundText} />}
    </div>
  );
});

SuggestField.displayName = 'SuggestField';
